import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, ShieldOff, ShieldCheck, FileBadge, Upload, Sparkles, Trash2, DollarSign, KeyRound, Copy } from "lucide-react";

const ROLES = ["vet", "fulfillment_staff", "admin"];
const EMPTY = { email: "", password: "", first_name: "", last_name: "", role: "vet" };

const ROLE_BADGE = {
  admin: "bg-paw-accent/15 text-paw-accent border-paw-accent/20",
  vet: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/20",
  fulfillment_staff: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/20",
  customer: "bg-black/[0.03] dark:bg-white/5 text-paw-text-muted dark:text-white/40 border-paw-border dark:border-white/10",
};

export default function AdminStaff() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [licenseFor, setLicenseFor] = useState(null);
  const [licFile, setLicFile] = useState(null);
  const [licUploading, setLicUploading] = useState(false);
  const [licResult, setLicResult] = useState(null);
  const [feeFor, setFeeFor] = useState(null);
  const [feeValue, setFeeValue] = useState("");
  const [feeSaving, setFeeSaving] = useState(false);
  const [centers, setCenters] = useState([]);
  const [resetLink, setResetLink] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users"); setUsers(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadCenters = async () => {
    try { const { data } = await api.get("/admin/fulfillment-centers"); setCenters(data || []); }
    catch (e) { /* non-fatal */ }
  };
  useEffect(() => { load(); loadCenters(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.role !== "fulfillment_staff") delete payload.center_id;
      await api.post("/admin/users", payload);
      toast.success(`${form.role} account created`);
      setCreating(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const resetPassword = async (userId, label) => {
    if (!window.confirm(`Generate a password reset link for ${label}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`);
      if (data?.reset_link) {
        setResetLink({ label, link: data.reset_link });
        toast.success("Reset link generated");
      } else {
        toast.success("Password reset email sent");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (userId, isActive) => {
    if (!window.confirm(`${isActive ? "Disable" : "Enable"} this user?`)) return;
    try { await api.patch(`/admin/users/${userId}/toggle-active`); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const removeUser = async (userId, label) => {
    if (!window.confirm(`Delete ${label}? The account is deactivated now and their data is kept for 30 days, then permanently removed. You can restore it within 30 days by re-enabling the account.`)) return;
    try { await api.delete(`/admin/users/${userId}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveFee = async (e) => {
    e.preventDefault();
    if (!feeFor) return;
    setFeeSaving(true);
    try {
      const key = feeFor.role === "vet" ? "signature_fee" : "flat_fee";
      await api.patch(`/admin/users/${feeFor.id}/pricing`, { [key]: Number(feeValue || 0) });
      toast.success("Fee updated");
      setFeeFor(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFeeSaving(false); }
  };

  const uploadLicense = async (e) => {
    e.preventDefault();
    if (!licFile || !licenseFor) return;
    setLicUploading(true); setLicResult(null);
    try {
      const fd = new FormData();
      fd.append("file", licFile);
      const { data } = await api.post(`/admin/users/${licenseFor.id}/upload-license`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLicResult(data.extraction);
      if (data.extraction.valid) toast.success("License verified by AI");
      else toast.warning("License could not be auto-verified");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLicUploading(false); }
  };

  const staff = users.filter((u) => ROLES.includes(u.role));
  const filtered = staff.filter((u) => filter === "all" || u.role === filter);

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-staff">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Access Control</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Staff</h1>
          <p className="font-manrope text-sm text-paw-text-secondary dark:text-white/50 mt-2">Admins, vets and fulfillment staff. Customer accounts live under Customers.</p>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover text-white font-manrope text-sm font-semibold transition-colors" data-testid="users-add">
          <Plus className="w-4 h-4" /> New Staff
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="users-filters">
        {["all", "admin", "vet", "fulfillment_staff"].map((r) => (
          <button key={r} onClick={() => setFilter(r)} className={`px-3 h-8 rounded-md text-xs font-manrope transition-colors ${filter === r ? "bg-paw-accent text-white" : "bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 text-paw-text-secondary dark:text-white/70"}`} data-testid={`users-filter-${r}`}>
            {r}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-paw-border dark:border-white/5">
              <tr className="text-left">
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Name</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Email</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Role</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Status</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Member since</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Fee</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id || u.email} className="border-b border-paw-border dark:border-white/5 last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]" data-testid={`user-row-${u.id || u.email}`}>
                  <td className="px-5 py-4 font-manrope text-sm">{u.first_name} {u.last_name}</td>
                  <td className="px-5 py-4 font-manrope text-sm text-paw-text-secondary dark:text-white/70">{u.email}</td>
                  <td className="px-5 py-4"><span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium ${ROLE_BADGE[u.role]}`}>{u.role}</span></td>
                  <td className="px-5 py-4 font-manrope text-xs">{u.is_active ? <span className="text-emerald-700 dark:text-emerald-400">Active</span> : <span className="text-red-700 dark:text-red-400">Disabled</span>}</td>
                  <td className="px-5 py-4 font-manrope text-xs text-paw-text-secondary dark:text-white/60" title={u.created_at || ""}>{u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "n/a"}</td>
                  <td className="px-5 py-4 font-manrope text-xs">
                    {u.role === "vet" && (u.signature_fee ? <span className="text-emerald-700 dark:text-emerald-300">${Number(u.signature_fee).toFixed(2)}/sig</span> : <span className="text-paw-text-muted dark:text-white/30">Not set</span>)}
                    {u.role === "fulfillment_staff" && (u.flat_fee ? <span className="text-emerald-700 dark:text-emerald-300">${Number(u.flat_fee).toFixed(2)}/order</span> : <span className="text-paw-text-muted dark:text-white/30">Not set</span>)}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {(u.role === "vet" || u.role === "fulfillment_staff") && u.id && (
                      <button onClick={() => { setFeeFor(u); setFeeValue(String(u.signature_fee || u.flat_fee || "")); }} className="p-2 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded-md transition-colors" title="Set payout fee" data-testid={`user-fee-${u.id}`}>
                        <DollarSign className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                      </button>
                    )}
                    {u.role === "vet" && (
                      <button onClick={() => { setLicenseFor(u); setLicFile(null); setLicResult(u.license_extraction || null); }} className="p-2 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded-md transition-colors" title="Upload / view license" data-testid={`user-license-${u.id}`}>
                        <FileBadge className={`w-4 h-4 ${u.license_verified ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`} />
                      </button>
                    )}
                    {u.id && (
                      <button onClick={() => resetPassword(u.id, `${u.first_name} ${u.last_name} (${u.email})`)} className="p-2 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded-md transition-colors" title="Reset password" data-testid={`user-reset-${u.id}`}>
                        <KeyRound className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                      </button>
                    )}
                    {u.id && (
                      <button onClick={() => toggle(u.id, u.is_active)} className="p-2 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded-md transition-colors" data-testid={`user-toggle-${u.id}`}>
                        {u.is_active ? <ShieldOff className="w-4 h-4 text-red-700 dark:text-red-400" /> : <ShieldCheck className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />}
                      </button>
                    )}
                    {u.id && u.role !== "admin" && (
                      <button onClick={() => removeUser(u.id, `${u.first_name} ${u.last_name} (${u.email})`)} className="p-2 hover:bg-red-500/10 rounded-md transition-colors" title="Delete user" data-testid={`user-delete-${u.id}`}>
                        <Trash2 className="w-4 h-4 text-red-700 dark:text-red-400" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setCreating(false)}>
          <form onSubmit={create} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="user-form">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-outfit font-bold text-xl">New Staff Account</h3>
              <button type="button" onClick={() => setCreating(false)} className="p-1 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { k: "first_name", l: "First name", t: "text" },
                { k: "last_name", l: "Last name", t: "text" },
              ].map(({ k, l, t }) => (
                <div key={k} className="space-y-1">
                  <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">{l}</label>
                  <input type={t} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid={`user-input-${k}`} />
                </div>
              ))}
            </div>
            <div className="space-y-1 mt-3">
              <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="user-input-email" />
            </div>
            <div className="space-y-1 mt-3">
              <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Password</label>
              <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="user-input-password" />
            </div>
            <div className="space-y-1 mt-3">
              <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="user-input-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {form.role === "fulfillment_staff" && (
              <div className="space-y-1 mt-3">
                <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Fulfillment center <span className="text-paw-text-muted dark:text-white/30 normal-case">(optional)</span></label>
                <select value={form.center_id || ""} onChange={(e) => setForm({ ...form, center_id: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="user-input-center">
                  <option value="">No center</option>
                  {centers.map((c) => <option key={c.center_id} value={c.center_id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <button type="submit" disabled={saving} className="w-full mt-5 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="user-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
            </button>
          </form>
        </div>
      )}

      {licenseFor && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setLicenseFor(null)}>
          <form onSubmit={uploadLicense} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="license-form">
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-outfit font-bold text-xl">Vet License Validation</h3>
              <button type="button" onClick={() => setLicenseFor(null)} className="p-1 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mb-5">Dr. {licenseFor.first_name} {licenseFor.last_name}</p>

            <label className={`block border-2 border-dashed ${licFile ? "border-paw-accent/50 bg-paw-accent/5" : "border-paw-border dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"} rounded-xl px-6 py-6 text-center cursor-pointer hover:border-paw-accent/50 transition-colors mb-4`}>
              <input type="file" accept="image/*,application/pdf" onChange={(e) => setLicFile(e.target.files?.[0] || null)} className="hidden" data-testid="license-file" />
              <FileBadge className="w-5 h-5 text-paw-text-muted dark:text-white/40 mx-auto mb-2" strokeWidth={1.5} />
              <p className="font-manrope text-sm">{licFile ? licFile.name : "Click to choose license image"}</p>
              <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1">JPG/PNG · Max 10MB</p>
            </label>

            {licResult && (
              <div className={`rounded-xl border p-4 mb-4 ${licResult.valid ? "bg-emerald-500/8 border-emerald-500/20" : "bg-amber-500/8 border-amber-500/20"}`} data-testid="license-result">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className={`w-4 h-4 ${licResult.valid ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`} />
                  <p className="font-manrope text-sm font-semibold">{licResult.valid ? "AI Verified" : "Needs Manual Review"}</p>
                </div>
                <dl className="grid grid-cols-2 gap-y-2 font-manrope text-xs">
                  <dt className="text-paw-text-muted dark:text-white/40">License #</dt><dd>{licResult.license_number || "n/a"}</dd>
                  <dt className="text-paw-text-muted dark:text-white/40">State</dt><dd>{licResult.state || "n/a"}</dd>
                  <dt className="text-paw-text-muted dark:text-white/40">Expiry</dt><dd>{licResult.expiry || "n/a"}</dd>
                  <dt className="text-paw-text-muted dark:text-white/40">Name</dt><dd>{licResult.name || "n/a"}</dd>
                </dl>
                {licResult.reason && <p className="font-manrope text-xs text-paw-text-muted dark:text-white/50 mt-3">{licResult.reason}</p>}
              </div>
            )}

            <button type="submit" disabled={!licFile || licUploading} className="w-full h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="license-submit">
              {licUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Validating with AI…</> : <><Upload className="w-4 h-4" /> Upload & Validate</>}
            </button>
          </form>
        </div>
      )}

      {feeFor && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setFeeFor(null)} data-testid="fee-modal">
          <form onSubmit={saveFee} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-outfit font-bold text-xl">Set Payout Fee</h3>
              <button type="button" onClick={() => setFeeFor(null)} className="p-1 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mb-5">{feeFor.first_name} {feeFor.last_name} <span className="text-paw-text-muted dark:text-white/30">·</span> {feeFor.role}</p>
            <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-xl p-4 mb-5">
              <p className="font-manrope text-xs text-emerald-300/90">
                {feeFor.role === "vet"
                  ? "This is what you owe this vet for every prescription they sign. Used in the Financials dashboard."
                  : "Flat fee per shipped order owed to this fulfillment staff member."}
              </p>
            </div>
            <div className="space-y-1">
              <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">{feeFor.role === "vet" ? "Per-signature fee (USD)" : "Per-order fee (USD)"}</label>
              <input type="number" step="0.01" min="0" value={feeValue} onChange={(e) => setFeeValue(e.target.value)} placeholder="e.g. 5.00" className="w-full h-11 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="fee-input" autoFocus />
            </div>
            <button type="submit" disabled={feeSaving} className="w-full mt-5 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="fee-save">
              {feeSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4" /> Save Fee</>}
            </button>
          </form>
        </div>
      )}

      {resetLink && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setResetLink(null)} data-testid="reset-link-modal">
          <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-outfit font-bold text-xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-700 dark:text-amber-400" /> Password Reset Link</h3>
              <button type="button" onClick={() => setResetLink(null)} className="p-1 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mb-5">{resetLink.label}</p>
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/50 mb-2">Copy this link and share it securely with the user. It lets them set a new password.</p>
            <div className="flex gap-2">
              <input readOnly value={resetLink.link} onClick={(e) => e.target.select()} className="flex-1 h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-xs font-manrope focus:outline-none focus:border-paw-accent" data-testid="reset-link-input" />
              <button onClick={() => { navigator.clipboard?.writeText(resetLink.link); toast.success("Copied to clipboard"); }} className="px-3 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover text-white font-manrope text-sm font-semibold flex items-center gap-2 transition-colors" data-testid="reset-link-copy">
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
